import { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Dialog } from '@mui/material';
import CaseDetail from '../CaseDetail';
import Download from '../Download';
import { getCaseColumns, getCaseGridTemplate } from '../columns';
import { getCaseDownloadPermissions } from '../../../helpers';
import './styles.scss';

const formatDate = (d) => (d ? moment(d).format('D MMM YYYY') : '-');

const formatPhone = (phn) => {
    if (!phn) return '';
    if (/^\([0-9]{3}\) [0-9]{3} [0-9]{4}$/.test(phn)) return phn;
    return `(${phn.slice(0, 3)}) ${phn.slice(3, 6)} ${phn.slice(6, 10)}`;
};

const formatArea = (city, state, zip) => {
    if (!city || !state) return '';
    return `${city}, ${state} ${zip || ''}`;
};

const trimId = (id) => (id.substring(0, 8));

const getStatusIcon = (status) => {
    if (status === 'Processing') {
        return <i className="material-icons status pending status-icon">timelapse</i>;
    }

    if (status === 'Submitted') {
        return <i className="material-icons status processing status-icon">timelapse</i>;
    }

    if (status === 'Accepted') {
        return <i className="material-icons status completed status-icon">check_circle_outline</i>;
    }

    // Otherwise, status === 'Processing Failed' or something not intended
    return <i className="material-icons status failed">warning</i>;
};

function Case({
    id,
    status,
    caseGroups,
    patientFirstName,
    patientLastName,
    doctorName,
    clinicName,
    clinic,
    labName,
    createdOn,
    receivedOn,
    dueOn,
    upperStlImageUrl,
    lowerStlImageUrl,
    fullStlImageUrl,
    upperPlyImageUrl,
    lowerPlyImageUrl,
    fullPlyImageUrl,
    orderIntegrationUploadZipUrl,
    prescriptionUrl,
    scanZipUrl,
    thumbnailUrl,
    template,
    instructions,
    labId,
    scans,
    numCases,
    index,
    labs,
    open,
    switchOpen,
    caseDetailGlobalState,
    setCaseDetailGlobalState,
    onCaseStatusChange,
    onCaseLabChange,
}) {
    const toggleHandler = (val) => {
        if (!open) switchOpen(index);
        else if (val === null) switchOpen(null);
        else switchOpen(val);
    };

    // Determine table columns
    const { scope } = localStorage;
    const scopeArray = (scope || '').split(' ');
    const hasAccess = (rule) => scopeArray.includes(rule);
    const canViewClinicsColumn = hasAccess('cases.display.clinics:read');
    const canViewLabsColumn = hasAccess('cases.display.labs:read');
    const columns = getCaseColumns(canViewClinicsColumn, canViewLabsColumn);
    const gridTemplateColumns = getCaseGridTemplate(columns);
    const {
        canDownloadOrderIntegrationUploadZip,
        canDownloadPrescription,
        canUpdateCaseStatus,
        canDownloadSTL,
    } = getCaseDownloadPermissions();
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const onCloseDownloadModal = () => setShowDownloadModal(false);
    const onOpenDownloadModal = () => setShowDownloadModal(true);

    const shortenId = (x) => x.substring(0, 8);
    const id8 = shortenId(id);

    const patientName = (`${patientFirstName || ''} ${patientLastName || ''}`).trim();
    const cells = {
        status: <span className="status-label">{status}</span>,
        patientName,
        doctorName,
        clinicName,
        labName,
        orderNumber: trimId(id),
        createdOn: formatDate(createdOn),
        receivedOn: formatDate(receivedOn),
        dueOn: formatDate(dueOn),
    };

    const isClickable = status !== null;
    const onOpenDownloadClick = (event) => {
        event.stopPropagation();
        onOpenDownloadModal();
    };
    const onRowClick = () => {
        if (!isClickable) {
            return;
        }

        toggleHandler();
    };
    const onRowKeyDown = (event) => {
        if (!isClickable || event.target !== event.currentTarget) {
            return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleHandler();
        }
    };

    return (
        <div className="case-row">
            <div
                style={{ gridTemplateColumns }}
                onClick={onRowClick}
                onKeyDown={onRowKeyDown}
                role="button"
                tabIndex={isClickable ? 0 : -1}
                aria-disabled={!isClickable}
                className={`cases-grid-row case-row-summary ${isClickable ? 'clickable' : ''}`}
            >
                {columns.map((column) => (
                    <div className={`data ${column.key}-cell`} key={column.key}>
                        {column.key === 'status' && (
                            <span className="status-icon-cell" aria-hidden="true">
                                {getStatusIcon(status)}
                            </span>
                        )}
                        {cells[column.key]}
                        {
                            column.key === 'clinicName' && (clinic?.address1 || clinic?.officePhone) && (
                                <span className="sub-text">
                                    {
                                        (clinic.address1 || clinic.address2) && (
                                            <>
                                                {clinic.address1 || ''}
                                                {clinic.address2 || ''}
                                                <br />
                                            </>
                                        )
                                    }
                                    {
                                        clinic.city && clinic.state && (
                                            <>
                                                {formatArea(clinic.city, clinic.state, clinic.zip)}
                                                <br />
                                            </>
                                        )
                                    }
                                    {clinic.officePhone ? formatPhone(clinic.officePhone) : ''}
                                </span>
                            )
                        }
                    </div>
                ))}
                <div className="data actions-cell">
                    <button type="button" className="downloads-button" onClick={onOpenDownloadClick} aria-label={`Open downloads for case ${id8}`}>
                        <i className="material-icons button" aria-hidden="true">more_horiz</i>
                    </button>
                </div>
            </div>
            {
                open && (
                    <CaseDetail
                        id={id}
                        groups={caseGroups}
                        upperStlImageUrl={upperStlImageUrl}
                        lowerStlImageUrl={lowerStlImageUrl}
                        fullStlImageUrl={fullStlImageUrl}
                        upperPlyImageUrl={upperPlyImageUrl}
                        lowerPlyImageUrl={lowerPlyImageUrl}
                        fullPlyImageUrl={fullPlyImageUrl}
                        orderIntegrationUploadZipUrl={orderIntegrationUploadZipUrl}
                        prescriptionUrl={prescriptionUrl}
                        thumbnailUrl={thumbnailUrl}
                        scanZipUrl={scanZipUrl}
                        template={template}
                        instructions={instructions}
                        patientName={patientName}
                        doctorName={doctorName}
                        labId={labId}
                        labName={labName}
                        clinicName={clinicName}
                        clinic={clinic}
                        formatArea={formatArea}
                        formatPhone={formatPhone}
                        status={status}
                        createdOn={createdOn}
                        receivedOn={receivedOn}
                        dueOn={dueOn}
                        scans={scans}
                        numCases={numCases}
                        index={index}
                        labs={labs}
                        setOpen={toggleHandler}
                        caseDetailGlobalState={caseDetailGlobalState}
                        setCaseDetailGlobalState={setCaseDetailGlobalState}
                        onCaseStatusChange={onCaseStatusChange}
                        onCaseLabChange={onCaseLabChange}
                    />
                )
            }
            <Dialog onClose={onCloseDownloadModal} open={showDownloadModal} style={{ zIndex: 9999 }}>
                <div className="downloads-popup">
                    <div className="title">
                        <h3>Downloads</h3>
                        <p>{`Case ID: ${id8}`}</p>
                    </div>
                    <div className="downloads">
                        <Download
                            id={id}
                            id8={id8}
                            orderIntegrationUploadZipUrl={orderIntegrationUploadZipUrl}
                            prescriptionUrl={prescriptionUrl}
                            scanZipUrl={scanZipUrl}
                            canDownloadOrderIntegrationUploadZip={canDownloadOrderIntegrationUploadZip}
                            canDownloadPrescription={canDownloadPrescription}
                            canUpdateCaseStatus={canUpdateCaseStatus}
                            canDownloadSTL={canDownloadSTL}
                            onCaseStatusChange={onCaseStatusChange}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

Case.propTypes = {
    id: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    caseGroups: PropTypes.arrayOf(
        PropTypes.object.isRequired,
    ),
    patientFirstName: PropTypes.string,
    patientLastName: PropTypes.string,
    doctorName: PropTypes.string,
    clinicName: PropTypes.string,
    clinic: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string,
        email: PropTypes.string,
        officePhone: PropTypes.string,
        cellPhone: PropTypes.string,
    }),
    labName: PropTypes.string,
    createdOn: PropTypes.string,
    receivedOn: PropTypes.string,
    dueOn: PropTypes.string,
    upperStlImageUrl: PropTypes.string,
    lowerStlImageUrl: PropTypes.string,
    fullStlImageUrl: PropTypes.string,
    upperPlyImageUrl: PropTypes.string,
    lowerPlyImageUrl: PropTypes.string,
    fullPlyImageUrl: PropTypes.string,
    orderIntegrationUploadZipUrl: PropTypes.string,
    prescriptionUrl: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    scanZipUrl: PropTypes.string,
    template: PropTypes.string,
    instructions: PropTypes.string,
    labId: PropTypes.string,
    scans: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        dentalCaseId: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        thumbnailUrl: PropTypes.string,
    })).isRequired,
    numCases: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
    labs: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    })),
    open: PropTypes.bool.isRequired,
    switchOpen: PropTypes.func.isRequired,
    caseDetailGlobalState: PropTypes.shape({
        isRightPanelOpen: PropTypes.bool.isRequired,
        rightPanel: PropTypes.number.isRequired,
        showRenderTips: PropTypes.bool.isRequired,
    }).isRequired,
    setCaseDetailGlobalState: PropTypes.func.isRequired,
    onCaseStatusChange: PropTypes.func,
    onCaseLabChange: PropTypes.func,
};

Case.defaultProps = {
    patientFirstName: '',
    patientLastName: '',
    doctorName: '',
    clinicName: null,
    clinic: null,
    labName: '',
    createdOn: '',
    receivedOn: '',
    dueOn: '',
    upperStlImageUrl: '',
    lowerStlImageUrl: '',
    fullStlImageUrl: '',
    upperPlyImageUrl: '',
    lowerPlyImageUrl: '',
    fullPlyImageUrl: '',
    orderIntegrationUploadZipUrl: '',
    prescriptionUrl: '',
    thumbnailUrl: '',
    scanZipUrl: '',
    template: '',
    instructions: '',
    labId: '',
    caseGroups: [],
    labs: [],
    onCaseStatusChange: () => {},
    onCaseLabChange: () => {},
};

export default Case;
